
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Rocket from 'plaid-threads/Icons/Rocket';
import Libraries from 'plaid-threads/Icons/Libraries';
import Overview from '../../../components/docs/Overview';
import Actions from '../../../components/docs/Actions';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Access data for credit cards, closed-end loans, and lines of credit'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Overview.Header product={'Liabilities'}>
  {metadata.description}
    </Overview.Header>
    <Actions.SmallWrapper miniTitle='Get started with Liabilities'>
  <Actions.SmallCard icon={<Libraries mdxType="Libraries" />} title='API Reference' link='/docs/api/products/liabilities/' />
  <Actions.SmallCard icon={<Rocket mdxType="Rocket" />} title='Quickstart' link='/docs/quickstart' />
    </Actions.SmallWrapper>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Plaid's Liabilities product allows you to access information about a user's debts. A common application is personal financial management tools to help customers manage or refinance debt. Liabilities is supported in the US and Canada (Canada coverage is limited).`}</p>
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <p>{`Below is a high level overview of the Liabilities integration process. For a more detailed walkthrough, see `}<a parentName="p" {...{
        "href": "/docs/liabilities/add-to-app"
      }}>{`Add Liabilities to your app`}</a>{`.`}</p>
    <Callout title="Linking multiple institutions in one session" mdxType="Callout">
      <p>{`If your use case involves seeing a user's full financial picture across multiple institutions, you can use `}<a parentName="p" {...{
          "href": "/docs/link/multi-item-link"
        }}>{`Multi-Item Link`}</a>{` to let users link multiple accounts in a single Link session.`}</p>
    </Callout>
    <ol>
      <li parentName="ol">{`Create a Link token by calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` with `}<inlineCode parentName="li">{`liabilities`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array.`}
        <ul parentName="li">
          <li parentName="ul">{`If your use case involves a user linking Items at multiple institutions, consider using `}<a parentName="li" {...{
              "href": "/docs/link/multi-item-link"
            }}>{`Multi-Item Link`}</a>{`.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Initialize Link with the Link token from the previous step. For more details, see `}<a parentName="li" {...{
          "href": "/docs/link"
        }}>{`Link`}</a>{`.`}</li>
      <li parentName="ol">{`Once the user has successfully completed the Link flow, exchange the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` for an `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` by calling `}<inlineCode parentName="li">{`/item/public_token/exchange`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/liabilities/get`}</inlineCode>{` with the `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` to retrieve Liabilities data for the Item.`}</li>
      <li parentName="ol">{`Listen for the `}<a parentName="li" {...{
          "href": "/docs/api/products/liabilities/#webhooks"
        }}><inlineCode parentName="a">{`DEFAULT_UPDATE`}</inlineCode></a>{` webhook to know when updated Liabilities data is available, then call `}<inlineCode parentName="li">{`/liabilities/get`}</inlineCode>{` again to retrieve it.`}</li>
    </ol>
    <h4 {...{
      "id": "liabilities-data"
    }}>{`Liabilities data`}</h4>
    <p>{`With Liabilities, you can view account information for credit cards, PayPal credit accounts, closed-end loans (student, mortgage, auto, personal, home equity, and commercial loans), and lines of credit, including home equity and commercial lines of credit. Available information includes balance, next payment date and amount, loan terms such as duration and interest rate, and originator information such as the origination date and initial loan amount. Liabilities data is refreshed approximately once per day, and the latest data can be accessed by calling `}<inlineCode parentName="p">{`/liabilities/get`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "liability-response-fields-by-account-subtype"
    }}>{`Liability response fields by account subtype`}</h5>
    <p>{`Liability data for each supported account subtype is returned in the following array on the `}<inlineCode parentName="p">{`liabilities`}</inlineCode>{` object:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Account type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Account subtypes`}</th>
          <th parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="th">{`/liabilities/get`}</inlineCode>{` field`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`credit`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`credit card`}</inlineCode>{`, `}<inlineCode parentName="td">{`paypal`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`credit`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`loan`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`mortgage`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`mortgage`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`loan`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`student`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`student`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`loan`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`auto`}</inlineCode>{`, `}<inlineCode parentName="td">{`commercial`}</inlineCode>{`, `}<inlineCode parentName="td">{`consumer`}</inlineCode>{`, `}<inlineCode parentName="td">{`home equity loan`}</inlineCode>{`, `}<inlineCode parentName="td">{`loan`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`loan`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`loan`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`commercial line of credit`}</inlineCode>{`, `}<inlineCode parentName="td">{`home equity`}</inlineCode>{`, `}<inlineCode parentName="td">{`line of credit`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`line_of_credit`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <p>{`Note that liabilities data does not contain detailed transaction history for credit card accounts. For credit card account transactions, use the `}<a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions`}</a>{` product.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample Liabilities data: credit card account",
        "#Sample": true,
        "Liabilities": true,
        "data:": true,
        "credit": true,
        "card": true,
        "account": true
      }}>{`"credit": [
  {
    "account_id": "dVzbVMLjrxTnLjX4G66XUp5GLklm4oiZy88yK",
    "aprs": [
      {
        "apr_percentage": 15.24,
        "apr_type": "balance_transfer_apr",
        "balance_subject_to_apr": 1562.32,
        "interest_charge_amount": 130.22
      },
      {
        "apr_percentage": 27.95,
        "apr_type": "cash_apr",
        "balance_subject_to_apr": 56.22,
        "interest_charge_amount": 14.81
      },
      {
        "apr_percentage": 12.5,
        "apr_type": "purchase_apr",
        "balance_subject_to_apr": 157.01,
        "interest_charge_amount": 25.66
      },
      {
        "apr_percentage": 0,
        "apr_type": "special",
        "balance_subject_to_apr": 1000,
        "interest_charge_amount": 0
      }
    ],
    "is_overdue": false,
    "last_payment_amount": 168.25,
    "last_payment_date": "2019-05-22",
    "last_statement_issue_date": "2019-05-28",
    "last_statement_balance": 1708.77,
    "minimum_payment_amount": 20,
    "next_payment_due_date": "2020-05-28"
  }
]
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample Liabilities data: student loan account",
        "#Sample": true,
        "Liabilities": true,
        "data:": true,
        "student": true,
        "loan": true,
        "account": true
      }}>{`"student": [
  {
    "account_id": "Pp1Vpkl9w8sajvK6oEEKtr7vZxBnGpf7LxxLE",
    "account_number": "4277075694",
    "disbursement_dates": [
      "2002-08-28"
    ],
    "expected_payoff_date": "2032-07-28",
    "guarantor": "DEPT OF ED",
    "interest_rate_percentage": 5.25,
    "is_overdue": false,
    "last_payment_amount": 138.05,
    "last_payment_date": "2019-04-22",
    "last_statement_issue_date": "2019-04-28",
    "loan_name": "Consolidation",
    "loan_status": {
      "end_date": "2032-07-28",
      "type": "repayment"
    },
    "minimum_payment_amount": 25,
    "next_payment_due_date": "2019-05-28",
    "origination_date": "2002-08-28",
    "origination_principal_amount": 25000,
    "outstanding_interest_amount": 6227.36,
    "payment_reference_number": "4277075694",
    "pslf_status": {
      // No longer populated: this object remains part of the schema, but its fields are now returned as null.
      "estimated_eligibility_date": null,
      "payments_made": null,
      "payments_remaining": null
    },
    "repayment_plan": {
      "description": "Standard Repayment",
      "type": "standard"
    },
    "sequence_number": "1",
    "servicer_address": {
      "city": "San Matias",
      "country": "US",
      "postal_code": "99415",
      "region": "CA",
      "street": "123 Relaxation Road"
    },
    "ytd_interest_paid": 280.55,
    "ytd_principal_paid": 271.65
  }
]
`}</code></pre>
    {/* prettier-ignore */}
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample Liabilities data: mortgage account",
        "#Sample": true,
        "Liabilities": true,
        "data:": true,
        "mortgage": true,
        "account": true
      }}>{`"mortgage": [
  {
    "account_id": "BxBXxLj1m4HMXBm9WZJyUg9XLd4rKEhw8Pb1J",
    "account_number": "3120194154",
    "current_late_fee": 25.00,
    "escrow_balance": 3141.54,
    "has_pmi": true,
    "has_prepayment_penalty": true,
    "interest_rate": {
      "percentage": 3.99,
      "type": "fixed"
    },
    "last_payment_amount": 3141.54,
    "last_payment_date": "2019-08-01",
    "loan_term": "30 year",
    "loan_type_description": "conventional",
    "maturity_date": "2045-07-31",
    "next_monthly_payment": 3141.54,
    "next_payment_due_date": "2019-11-15",
    "origination_date": "2015-08-01",
    "origination_principal_amount": 425000,
    "past_due_amount": 2304,
    "property_address": {
      "city": "Malakoff",
      "country": "US",
      "postal_code": "14236",
      "region": "NY",
      "street": "2992 Cameron Road"
    },
    "ytd_interest_paid": 12300.40,
    "ytd_principal_paid": 12340.50
  }
]
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample Liabilities data: closed-end loan account",
        "#Sample": true,
        "Liabilities": true,
        "data:": true,
        "closed-end": true,
        "loan": true,
        "account": true
      }}>{`"loan": [
  {
    "account_id": "c0cTGbrDXN3xiGLGUNI9LGwxrOGjakxtWTgQV",
    "account_number": "8943210074",
    "interest_rate": {
      "percentage": 6.49,
      "type": "fixed"
    },
    "principal_balance": 18250,
    "origination_principal_amount": 32000,
    "origination_date": "2023-09-01",
    "loan_term_months": 72,
    "maturity_date": "2029-09-01",
    "payment_count": 72,
    "payment_frequency": "monthly",
    "next_payment_amount": 525.4,
    "next_payment_due_date": "2026-07-01",
    "last_payment_amount": 525.4,
    "last_payment_date": "2026-06-01",
    "payoff_amount": 18190.25,
    "ytd_interest_paid": 612.18,
    "compounding_period": "monthly"
  }
]
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample Liabilities data: line of credit account",
        "#Sample": true,
        "Liabilities": true,
        "data:": true,
        "line": true,
        "of": true,
        "credit": true,
        "account": true
      }}>{`"line_of_credit": [
  {
    "account_id": "ufQuuYHpSjhuk9TKIQTnDWlFVDIR5nWSynuQD",
    "interest_rate": {
      "percentage": 8.25,
      "type": "variable"
    },
    "credit_limit": 100000,
    "available_credit": 76000,
    "principal_balance": 24000,
    "minimum_payment_amount": 180,
    "next_payment_amount": 180,
    "next_payment_due_date": "2026-07-05",
    "last_payment_amount": 180,
    "last_payment_date": "2026-06-05",
    "last_statement_balance": 24000,
    "last_statement_issue_date": "2026-06-01",
    "past_due_amount": 0,
    "finance_charges": 162.5,
    "draw_period_end_date": "2032-03-10",
    "aprs": [
      {
        "apr_percentage": 8.25,
        "apr_type": "purchase_apr",
        "balance_subject_to_apr": null,
        "interest_charge_amount": null
      }
    ]
  }
]
`}</code></pre>
    <h4 {...{
      "id": "payment-history"
    }}>{`Payment history`}</h4>
    <p>{`The information returned by a `}<inlineCode parentName="p">{`/liabilities/get`}</inlineCode>{` request contains recent payment information, such as the date and amount of the most recent payment. To view further payment history, you can use Plaid's `}<a parentName="p" {...{
        "href": "/docs/transactions"
      }}>{`Transactions`}</a>{` product.`}</p>
    <h4 {...{
      "id": "liabilities-webhooks"
    }}>{`Liabilities webhooks`}</h4>
    <p>{`Plaid checks for updated Liabilities data approximately once per day and uses webhooks to inform you of any changes so you can keep your app up to date. For more detail on how to listen and respond to these webhooks, see `}<a parentName="p" {...{
        "href": "/docs/api/products/liabilities/#webhooks"
      }}>{`Liabilities webhooks`}</a>{`.`}</p>
    <h4 {...{
      "id": "testing-liabilities"
    }}>{`Testing Liabilities`}</h4>
    <p>{`Liabilities can be tested in `}<a parentName="p" {...{
        "href": "/docs/sandbox"
      }}>{`Sandbox`}</a>{` without any additional permissions.`}</p>
    <p>{`To test general-purpose, commercial, and home-equity loans and lines of credit in Sandbox, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials/#liabilities-testing-credentials"
      }}>{`Liabilities testing credentials`}</a>{`.`}</p>
    <p>{`To test student loan accounts beyond those offered by the default Sandbox user, see `}<a parentName="p" {...{
        "href": "/docs/sandbox/user-custom/#configuring-the-custom-user-account"
      }}>{`Configuring the custom user account`}</a>{`.`}</p>
    <h4 {...{
      "id": "liabilities-pricing"
    }}>{`Liabilities pricing`}</h4>
    <p>{`Liabilities is billed on a `}<a parentName="p" {...{
        "href": "/docs/account/billing/#subscription-fee"
      }}>{`subscription model`}</a>{`. To view the exact pricing you may be eligible for, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`apply for Production access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. For more details about pricing and billing models, see `}<a parentName="p" {...{
        "href": "/docs/account/billing"
      }}>{`Plaid billing`}</a>{`.`}</p>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`To get started building with Liabilities, see `}<a parentName="p" {...{
        "href": "/docs/liabilities/add-to-app"
      }}>{`Add Liabilities to your App`}</a>{`.`}</p>
    <p>{`If you're ready to launch to Production, see the `}<a parentName="p" {...{
        "href": "/docs/launch-checklist/"
      }}>{`Launch checklist`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Launch Center' subtitle="See next steps to launch in Production" link='https://dashboard.plaid.com/developers/launch-center' linkText='Launch' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "liabilities-data",
        level: 4,
        title: "Liabilities data",
      },
{
        id: "payment-history",
        level: 4,
        title: "Payment history",
      },
{
        id: "liabilities-webhooks",
        level: 4,
        title: "Liabilities webhooks",
      },
{
        id: "testing-liabilities",
        level: 4,
        title: "Testing Liabilities",
      },
{
        id: "liabilities-pricing",
        level: 4,
        title: "Liabilities pricing",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
